import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Download,
  BarChart3,
  Activity,
  DollarSign,
  Clock,
  CheckCircle,
  AlertTriangle,
  Package,
  RefreshCw,
  Wrench,
  FileSpreadsheet,
  FileText,
  ChevronDown
} from 'lucide-react';
import { reportService, DATE_RANGE_OPTIONS } from '../../../services/reportService';
import { PERMISSIONS } from '../../../constants/permissions';
import PermissionGuard from '../../../components/PermissionGuard';
import { exportToCSV, exportToExcel, formatCurrencyForExport, formatPercentageForExport, getDateString } from '../../../utils/exportUtils';

const Reports = () => {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Report data
  const [summary, setSummary] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [assetPerformance, setAssetPerformance] = useState([]);

  // Export functions
  const handleExportSummary = (format) => {
    const data = [
      {
        metric: 'Total Work Orders',
        value: summary?.totalWorkOrders || 0,
      },
      {
        metric: 'Completed Work Orders',
        value: summary?.completedWorkOrders || 0,
      },
      {
        metric: 'Completion Rate',
        value: formatPercentageForExport(summary?.completionRate),
      },
      {
        metric: 'Average Response Time (hours)',
        value: summary?.averageResponseTimeHours || 0,
      },
      {
        metric: 'Total Assets',
        value: summary?.totalAssets || 0,
      },
      {
        metric: 'Operational Assets',
        value: summary?.operationalAssets || 0,
      },
      {
        metric: 'Asset Uptime Rate',
        value: formatPercentageForExport(summary?.assetUptimeRate),
      },
      {
        metric: 'Total Cost',
        value: formatCurrencyForExport(summary?.totalCost),
      },
      {
        metric: 'Labor Cost',
        value: formatCurrencyForExport(summary?.laborCost),
      },
      {
        metric: 'Parts Cost',
        value: formatCurrencyForExport(summary?.partsCost),
      },
    ];

    const columns = [
      { key: 'metric', label: 'Metric' },
      { key: 'value', label: 'Value' },
    ];

    const filename = `report-summary-${getDateString()}`;
    if (format === 'csv') {
      exportToCSV(data, columns, filename);
    } else {
      exportToExcel(data, columns, filename);
    }
    setShowExportMenu(false);
  };

  const handleExportMonthly = (format) => {
    const data = monthlyData.map(m => ({
      month: m.month,
      workOrdersCreated: m.workOrdersCreated || 0,
      workOrdersCompleted: m.workOrdersCompleted || 0,
      completionRate: formatPercentageForExport(m.completionRate),
      totalCost: formatCurrencyForExport(m.totalCost),
      laborCost: formatCurrencyForExport(m.laborCost),
      partsCost: formatCurrencyForExport(m.partsCost),
      avgResponseTime: m.averageResponseTimeHours || 0,
    }));

    const columns = [
      { key: 'month', label: 'Month' },
      { key: 'workOrdersCreated', label: 'Work Orders Created' },
      { key: 'workOrdersCompleted', label: 'Work Orders Completed' },
      { key: 'completionRate', label: 'Completion Rate' },
      { key: 'totalCost', label: 'Total Cost' },
      { key: 'laborCost', label: 'Labor Cost' },
      { key: 'partsCost', label: 'Parts Cost' },
      { key: 'avgResponseTime', label: 'Avg Response Time (hrs)' },
    ];

    const filename = `monthly-report-${getDateString()}`;
    if (format === 'csv') {
      exportToCSV(data, columns, filename);
    } else {
      exportToExcel(data, columns, filename);
    }
    setShowExportMenu(false);
  };

  const handleExportAssets = (format) => {
    const data = assetPerformance.map(a => ({
      assetName: a.assetName || '',
      assetTag: a.assetTag || '',
      status: a.status || '',
      category: a.category || '',
      totalWorkOrders: a.totalWorkOrders || 0,
      completedWorkOrders: a.completedWorkOrders || 0,
      openWorkOrders: a.openWorkOrders || 0,
      mttr: a.mttr || 0,
      avgResponseTime: a.averageResponseTime || 0,
      totalCost: formatCurrencyForExport(a.totalMaintenanceCost),
      laborCost: formatCurrencyForExport(a.laborCost),
      partsCost: formatCurrencyForExport(a.partsCost),
      uptime: formatPercentageForExport(a.uptimePercentage),
    }));

    const columns = [
      { key: 'assetName', label: 'Asset Name' },
      { key: 'assetTag', label: 'Asset Tag' },
      { key: 'status', label: 'Status' },
      { key: 'category', label: 'Category' },
      { key: 'totalWorkOrders', label: 'Total Work Orders' },
      { key: 'completedWorkOrders', label: 'Completed' },
      { key: 'openWorkOrders', label: 'Open' },
      { key: 'mttr', label: 'MTTR (hrs)' },
      { key: 'avgResponseTime', label: 'Avg Response Time (hrs)' },
      { key: 'totalCost', label: 'Total Cost' },
      { key: 'laborCost', label: 'Labor Cost' },
      { key: 'partsCost', label: 'Parts Cost' },
      { key: 'uptime', label: 'Uptime' },
    ];

    const filename = `asset-performance-${getDateString()}`;
    if (format === 'csv') {
      exportToCSV(data, columns, filename);
    } else {
      exportToExcel(data, columns, filename);
    }
    setShowExportMenu(false);
  };

  const handleExportAll = (format) => {
    handleExportSummary(format);
    setTimeout(() => handleExportMonthly(format), 500);
    setTimeout(() => handleExportAssets(format), 1000);
  };

  useEffect(() => {
    fetchReportData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, [days]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [summaryRes, monthlyRes, assetRes] = await Promise.all([
        reportService.getReportSummary(days),
        reportService.getMonthlyReport(days > 90 ? 12 : 6),
        reportService.getAssetPerformance(days)
      ]);

      setSummary(summaryRes);
      setMonthlyData(monthlyRes?.monthlyData || []);
      setAssetPerformance(assetRes?.assets || []);
    } catch (err) {
      console.error('Failed to load report data:', err);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getTypeColor = (type) => {
    const colors = {
      PREVENTIVE: 'bg-blue-500',
      CORRECTIVE: 'bg-yellow-500',
      INSPECTION: 'bg-purple-500',
      PROJECT: 'bg-green-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      URGENT: 'bg-red-500',
      HIGH: 'bg-orange-500',
      MEDIUM: 'bg-yellow-500',
      LOW: 'bg-green-500'
    };
    return colors[priority] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // KPI metrics from summary
  const kpiMetrics = [
    {
      name: t('cmms.rpt_avgResponseTime'),
      value: summary?.averageResponseTimeHours ? `${summary.averageResponseTimeHours}h` : '0h',
      icon: Clock,
      color: 'bg-blue-500',
      description: t('cmms.rpt_timeFromCreation')
    },
    {
      name: t('cmms.rpt_totalCost'),
      value: formatCurrency(summary?.totalCost),
      icon: DollarSign,
      color: 'bg-green-500',
      description: t('cmms.rpt_maintenanceCosts')
    },
    {
      name: t('cmms.rpt_completionRate'),
      value: summary?.completionRate ? `${summary.completionRate}%` : '0%',
      icon: CheckCircle,
      color: 'bg-purple-500',
      description: t('cmms.rpt_completedOf', { completed: summary?.completedWorkOrders || 0, total: summary?.totalWorkOrders || 0 })
    },
    {
      name: t('cmms.rpt_assetUptime'),
      value: summary?.assetUptimeRate ? `${summary.assetUptimeRate}%` : '0%',
      icon: Activity,
      color: 'bg-orange-500',
      description: t('cmms.rpt_operationalOf', { operational: summary?.operationalAssets || 0, total: summary?.totalAssets || 0 })
    },
  ];

  // Cost breakdown
  const costBreakdown = [
    { category: t('cmms.rpt_labor'), amount: summary?.laborCost || 0 },
    { category: t('cmms.rpt_parts'), amount: summary?.partsCost || 0 },
    { category: t('cmms.rpt_other'), amount: (summary?.totalCost || 0) - (summary?.laborCost || 0) - (summary?.partsCost || 0) }
  ].filter(item => item.amount > 0);

  const totalCostForBreakdown = costBreakdown.reduce((sum, item) => sum + Number(item.amount), 0) || 1;

  return (
    <PermissionGuard permission={PERMISSIONS.VIEW_REPORTS}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('cmms.reports')}</h1>
            <p className="text-gray-600 mt-1">{t('cmms.rpt_subtitle')}</p>
          </div>
          <div className="flex gap-3">
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-lg"
            >
              {DATE_RANGE_OPTIONS.map(option => (
                <option key={option.value} value={option.days}>{option.label}</option>
              ))}
            </select>
            <button onClick={fetchReportData} className="btn btn-outline">
              <RefreshCw className="w-5 h-5 mr-2" />
              {t('common.refresh')}
            </button>

            {/* Export Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="btn btn-primary flex items-center"
              >
                <Download className="w-5 h-5 mr-2" />
                {t('common.export')}
                <ChevronDown className="w-4 h-4 ml-2" />
              </button>

              {showExportMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowExportMenu(false)}
                  />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">{t('common.exportExcel')}</div>
                    <button
                      onClick={() => handleExportAll('excel')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      {t('cmms.rpt_allReportsXlsx')}
                    </button>
                    <button
                      onClick={() => handleExportSummary('excel')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      {t('cmms.rpt_summaryReport')}
                    </button>
                    <button
                      onClick={() => handleExportMonthly('excel')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      {t('cmms.rpt_monthlyTrends')}
                    </button>
                    <button
                      onClick={() => handleExportAssets('excel')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      {t('cmms.rpt_assetPerformance')}
                    </button>

                    <div className="border-t border-gray-200 my-2"></div>

                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">{t('common.exportCsv')}</div>
                    <button
                      onClick={() => handleExportAll('csv')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4 text-blue-600" />
                      {t('cmms.rpt_allReportsCsv')}
                    </button>
                    <button
                      onClick={() => handleExportSummary('csv')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4 text-blue-600" />
                      {t('cmms.rpt_summaryReport')}
                    </button>
                    <button
                      onClick={() => handleExportMonthly('csv')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4 text-blue-600" />
                      {t('cmms.rpt_monthlyTrends')}
                    </button>
                    <button
                      onClick={() => handleExportAssets('csv')}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4 text-blue-600" />
                      {t('cmms.rpt_assetPerformance')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpiMetrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <div key={metric.name} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-12 h-12 ${metric.color} rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{metric.value}</div>
                <div className="text-sm text-gray-600">{metric.name}</div>
                <div className="text-xs text-gray-500 mt-1">{metric.description}</div>
              </div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Work Orders by Type */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">{t('cmms.rpt_workOrdersByType')}</h2>
            {summary?.workOrdersByType?.length > 0 ? (
              <div className="space-y-4">
                {summary.workOrdersByType.map((item) => (
                  <div key={item.type}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">{item.type}</span>
                      <span className="text-sm text-gray-600">{item.count} ({Math.round(item.percentage)}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`${getTypeColor(item.type)} h-3 rounded-full transition-all`}
                        style={{ width: `${item.percentage}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Wrench className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{t('cmms.rpt_noWorkOrderData')}</p>
              </div>
            )}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{t('cmms.workOrders')}</span>
                <span className="text-2xl font-bold text-gray-900">{summary?.totalWorkOrders || 0}</span>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">{t('cmms.rpt_costBreakdown')}</h2>
            {costBreakdown.length > 0 ? (
              <div className="space-y-4">
                {costBreakdown.map((item, index) => {
                  const percentage = Math.round((Number(item.amount) / totalCostForBreakdown) * 100);
                  const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500'];
                  return (
                    <div key={item.category} className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 ${colors[index % colors.length]} rounded-full`}></div>
                        <span className="font-medium text-gray-900">{item.category}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-gray-900">{formatCurrency(item.amount)}</div>
                        <div className="text-xs text-gray-500">{percentage}%</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{t('cmms.rpt_noCostData')}</p>
              </div>
            )}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">{t('common.total')}</span>
                <span className="text-2xl font-bold text-gray-900">{formatCurrency(summary?.totalCost)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Trends */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">{t('cmms.rpt_monthlyTrends')}</h2>
          {monthlyData.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <div className="flex items-end justify-between gap-4 min-w-[600px] h-64">
                  {monthlyData.map((month) => {
                    const maxWorkOrders = Math.max(...monthlyData.map(m => m.workOrdersCreated || 0), 1);
                    const height = ((month.workOrdersCreated || 0) / maxWorkOrders) * 100;

                    return (
                      <div key={`${month.year}-${month.monthNumber}`} className="flex-1 flex flex-col items-center">
                        <div className="w-full flex flex-col items-center gap-2 mb-2">
                          <div className="text-xs font-medium text-gray-600">{month.workOrdersCreated || 0}</div>
                          <div
                            className="w-full bg-primary-500 rounded-t-lg transition-all hover:bg-primary-600 cursor-pointer relative group"
                            style={{ height: `${Math.max(height, 5)}%`, minHeight: '20px' }}
                          >
                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                              {t('cmms.rpt_tooltipCreated')}: {month.workOrdersCreated}<br/>
                              {t('cmms.rpt_tooltipCompleted')}: {month.workOrdersCompleted}<br/>
                              {t('cmms.rpt_tooltipCost')}: {formatCurrency(month.totalCost)}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm font-medium text-gray-700">{month.month?.split(' ')[0]}</div>
                        <div className="text-xs text-gray-500">{formatCurrency(month.totalCost)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="mt-6 flex items-center justify-center gap-8 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-primary-500 rounded"></div>
                  <span className="text-gray-600">{t('cmms.rpt_woCreated')}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>{t('cmms.rpt_noMonthlyData')}</p>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Asset Performance */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">{t('cmms.rpt_assetPerfTitle')}</h2>
              <p className="text-sm text-gray-500 mt-1">{t('cmms.rpt_topAssets')}</p>
            </div>
            {assetPerformance.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.rpt_asset')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.rpt_wos')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">MTTR</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('cmms.rpt_totalCost')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {assetPerformance.slice(0, 10).map((asset) => (
                      <tr key={asset.assetId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{asset.assetName}</div>
                          <div className="text-xs text-gray-500">{asset.assetTag}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            asset.status === 'OPERATIONAL' ? 'bg-green-100 text-green-800' :
                            asset.status === 'MAINTENANCE' ? 'bg-yellow-100 text-yellow-800' :
                            asset.status === 'DOWN' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {asset.status?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {asset.totalWorkOrders}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {asset.mttr ? `${asset.mttr}h` : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(asset.totalMaintenanceCost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{t('cmms.rpt_noAssetData')}</p>
              </div>
            )}
          </div>

          {/* Work Orders by Priority */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">{t('cmms.rpt_workOrdersByPriority')}</h2>
            {summary?.workOrdersByPriority?.length > 0 ? (
              <div className="space-y-4">
                {summary.workOrdersByPriority.map((item) => (
                  <div key={item.priority} className="flex items-center gap-4 p-4 rounded-lg border border-gray-200">
                    <div className={`w-10 h-10 ${getPriorityColor(item.priority)} rounded-lg flex items-center justify-center`}>
                      <AlertTriangle className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{item.priority}</div>
                      <div className="text-sm text-gray-600">
                        {item.count} {t('cmms.rpt_workOrdersCount')} ({Math.round(item.percentage)}%)
                      </div>
                    </div>
                    <div className="text-2xl font-bold text-gray-900">{item.count}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{t('cmms.rpt_noPriorityData')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="bg-gradient-to-r from-primary-600 to-blue-600 rounded-xl p-6 text-white">
          <h2 className="text-2xl font-bold mb-6">{t('cmms.rpt_periodSummary')}</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Wrench className="w-6 h-6" />
              </div>
              <div className="text-3xl font-bold mb-1">{summary?.totalWorkOrders || 0}</div>
              <div className="text-sm opacity-80">{t('cmms.workOrders')}</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                <CheckCircle className="w-6 h-6" />
              </div>
              <div className="text-3xl font-bold mb-1">{summary?.completedWorkOrders || 0}</div>
              <div className="text-sm opacity-80">{t('cmms.completed')}</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                <Package className="w-6 h-6" />
              </div>
              <div className="text-3xl font-bold mb-1">{summary?.totalAssets || 0}</div>
              <div className="text-sm opacity-80">{t('cmms.totalAssets')}</div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                <DollarSign className="w-6 h-6" />
              </div>
              <div className="text-3xl font-bold mb-1">{formatCurrency(summary?.totalCost)}</div>
              <div className="text-sm opacity-80">{t('cmms.rpt_totalSpent')}</div>
            </div>
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
};

export default Reports;
